import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import useCheckoutQueryParams from "@hooks/useCheckoutQueryParams";

import { checkoutAtom } from "@/atoms/checkoutAtom";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAtom } from "jotai";

import { Box, Flex } from "@chakra-ui/react";

import { CheckoutPageRoutesEnum } from "@ts/enums/CheckoutPageRoutesEnum";
import { ClassesEnum } from "@ts/enums/ClassesEnum";

import linkSvg from "@assets/img/link.svg";

import { formatPrice, getTrackingId } from "@utils/utils";

import { Text } from "@components/Text";

interface PropTypes {
    product: {
        name: string;
        price: number;
        grade: string;
        isEditable: boolean;
        isAlreadyPurchased: boolean;
    };
    addon: {
        name: string;
        price: number;
        isEditable: boolean;
    } | null;
    discount?: {
        name: string;
        price: number;
        isEditable: boolean;
    };
    onEditAppliedDiscount?: () => void;
}

const CheckoutSummary = ({
    product,
    addon,
    discount,
    onEditAppliedDiscount
}: PropTypes) => {
    const { t } = useTranslation();

    const navigate = useNavigate();

    const { routeWithQueryParams } = useCheckoutQueryParams();

    const [checkout] = useAtom(checkoutAtom);

    const selectedName = checkout.selectedStudent
        ? checkout.selectedStudent.first_name
        : null;

    return (
        <Flex direction="column">
            <Flex
                w="full"
                py={3}
                direction="column"
                borderBottom="1px solid"
                borderColor="gray.200"
            >
                <Text color="gray.600" fontSize={12} textTransform="uppercase">
                    {selectedName ? `Program for ${selectedName}` : "Program 1"}
                </Text>
                <Flex
                    justify="space-between"
                    position="relative"
                    _before={
                        addon
                            ? {
                                  content: `url('${linkSvg}')`,
                                  position: "absolute",
                                  bottom: "-20px",
                                  left: "2px"
                              }
                            : {}
                    }
                >
                    <Text
                        type="subHeading"
                        fontFamily="inter, sans-serif"
                        fontSize={14}
                    >
                        {product.name}: {product.grade}
                    </Text>
                    <Flex justify="center" align="center" gap={3}>
                        {product.isAlreadyPurchased ? (
                            <>
                                <Text
                                    fontFamily="inter, sans-serif"
                                    fontSize={14}
                                >
                                    Purchased
                                </Text>
                                <Box>
                                    <FontAwesomeIcon
                                        icon={faPenToSquare}
                                        color="white"
                                        fontSize={14}
                                    />
                                </Box>
                            </>
                        ) : (
                            <>
                                <Text
                                    fontFamily="inter, sans-serif"
                                    fontSize={14}
                                >
                                    {formatPrice(product.price)}
                                </Text>
                                <Box
                                    opacity={product.isEditable ? "1" : "0.5"}
                                    layerStyle="checkoutInfoEditLayoutStyle"
                                    className={ClassesEnum.EditButton}
                                    data-tracking-id={getTrackingId(
                                        [
                                            ClassesEnum.PaymentDetailsContainer,
                                            ClassesEnum.EditButton
                                        ],
                                        "changePlan"
                                    )}
                                    cursor={
                                        product.isEditable
                                            ? "pointer"
                                            : "not-allowed"
                                    }
                                    onClick={
                                        product.isEditable
                                            ? () =>
                                                  navigate(
                                                      routeWithQueryParams(
                                                          CheckoutPageRoutesEnum.PRODUCT_FAMILY_SELECT_PAGE
                                                      )
                                                  )
                                            : undefined
                                    }
                                >
                                    <FontAwesomeIcon
                                        icon={faPenToSquare}
                                        color="var(--euka-colors-burntYellow-500)"
                                        fontSize={14}
                                    />
                                </Box>
                            </>
                        )}
                    </Flex>
                </Flex>
                {addon && (
                    <Flex direction="column" pl={4} mt={-1}>
                        <Text color="gray.600" fontSize={12}>
                            {t(
                                "heckout.checkoutPage.premiumServices",
                                "Premium Services"
                            )}
                        </Text>
                        <Flex justify="space-between" mt={-1}>
                            <Text fontFamily="inter, sans-serif" fontSize={14}>
                                {addon.name}
                            </Text>
                            <Flex justify="center" align="center" gap={3}>
                                <Text
                                    fontFamily="inter, sans-serif"
                                    fontSize={14}
                                >
                                    {formatPrice(addon.price)}
                                </Text>
                                <Box
                                    opacity={addon.isEditable ? "1" : "0.5"}
                                    layerStyle="checkoutInfoEditLayoutStyle"
                                    className={ClassesEnum.EditButton}
                                    data-tracking-id={getTrackingId(
                                        [
                                            ClassesEnum.PaymentDetailsContainer,
                                            ClassesEnum.EditButton
                                        ],
                                        "changeAddon"
                                    )}
                                    cursor={
                                        addon.isEditable
                                            ? "pointer"
                                            : "not-allowed"
                                    }
                                    onClick={
                                        addon.isEditable
                                            ? () =>
                                                  navigate(
                                                      routeWithQueryParams(
                                                          CheckoutPageRoutesEnum.ADDON_SELECT_PAGE
                                                      )
                                                  )
                                            : undefined
                                    }
                                >
                                    <FontAwesomeIcon
                                        icon={faPenToSquare}
                                        color="var(--euka-colors-burntYellow-500)"
                                        fontSize={14}
                                    />
                                </Box>
                            </Flex>
                        </Flex>
                    </Flex>
                )}
            </Flex>
            {discount && (
                <Flex
                    w="full"
                    py={3}
                    direction="column"
                    borderBottom="1px solid"
                    borderColor="gray.200"
                >
                    <Flex justify="space-between">
                        <Text
                            type="subHeading"
                            fontFamily="inter, sans-serif"
                            fontSize={14}
                        >
                            Discount applied
                        </Text>
                        <Flex justify="center" align="center" gap={3}>
                            <Text fontFamily="inter, sans-serif" fontSize={14}>
                                -{formatPrice(discount.price)}
                            </Text>
                            <Box
                                opacity={discount.isEditable ? "1" : "0.5"}
                                layerStyle="checkoutInfoEditLayoutStyle"
                                className={ClassesEnum.EditButton}
                                data-tracking-id={getTrackingId(
                                    [
                                        ClassesEnum.PaymentDetailsContainer,
                                        ClassesEnum.EditButton
                                    ],
                                    "changeCouponCode"
                                )}
                                cursor={
                                    discount.isEditable
                                        ? "pointer"
                                        : "not-allowed"
                                }
                                onClick={
                                    discount.isEditable
                                        ? onEditAppliedDiscount
                                        : undefined
                                }
                            >
                                <FontAwesomeIcon
                                    data-testid="changeItem"
                                    icon={faPenToSquare}
                                    color="var(--euka-colors-burntYellow-500)"
                                    fontSize={14}
                                />
                            </Box>
                        </Flex>
                    </Flex>
                    {addon && (
                        <Flex direction="column" mt={-1}>
                            <Text color="gray.600" fontSize={12}>
                                {discount.name}
                            </Text>
                        </Flex>
                    )}
                </Flex>
            )}
        </Flex>
    );
};

export default CheckoutSummary;
